import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { format } from "date-fns";
import { useAllInfluencers } from "@/hooks/useAllInfluencers";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { convertDisplayToDb } from "@/utils/currency";
import { useAddPayment } from "../../hooks/useAddPayment";
import { calculateTaxAmounts } from "../../services/taxCalculation.service";
import {
  type AddPaymentModalProps,
  type Campaign,
  type CampaignInfluencerFromDB,
  type PaymentValidationErrors,
  type PaymentStatus,
  type PaymentMode,
  InfluencerCampaignSelector,
  TaxBreakdownCard,
  PaymentScheduleFields,
} from "./add-modal";

export type { AddPaymentModalProps, Campaign, CampaignInfluencerFromDB };

export const AddPaymentModal: React.FC<AddPaymentModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { userCountry } = useAuth();
  const [influencerId, setInfluencerId] = useState<string>("");
  const [campaignInfluencerId, setCampaignInfluencerId] = useState<string>("");
  const [baseFee, setBaseFee] = useState<string>("");
  const [status, setStatus] = useState<string>("pending");
  const [paymentDate, setPaymentDate] = useState<Date | undefined>(undefined);
  const [dueDate, setDueDate] = useState<Date | undefined>(undefined);
  const [mode, setMode] = useState<string>("upi");
  const [transactionId, setTransactionId] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [filteredCampaignInfluencers, setFilteredCampaignInfluencers] = useState<
    CampaignInfluencerFromDB[]
  >([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);

  // GST related states
  const [includeGst, setIncludeGst] = useState<boolean>(false);
  const [gstPercentage, setGstPercentage] = useState<string>("18.0");
  const [gstAmount, setGstAmount] = useState<number>(0);

  // TDS related states
  const [includeTds, setIncludeTds] = useState<boolean>(false);
  const [tdsPercentage, setTdsPercentage] = useState<string>("10.0");
  const [tdsAmount, setTdsAmount] = useState<number>(0);

  // Calculated amounts
  const [netPayable, setNetPayable] = useState<number>(0);
  const [errors, setErrors] = useState<PaymentValidationErrors>({});

  // Fetch all influencers from Supabase
  const { data: influencers, isLoading: loadingInfluencers } =
    useAllInfluencers();

  // Use the domain useAddPayment hook
  const addPaymentMutation = useAddPayment();

  // Calculate GST, TDS, and net payable amounts
  useEffect(() => {
    const base = parseFloat(baseFee) || 0;
    const result = calculateTaxAmounts({
      baseFee: base,
      includeGst,
      gstPercentage: parseFloat(gstPercentage) || 0,
      includeTds,
      tdsPercentage: parseFloat(tdsPercentage) || 0,
    });

    setGstAmount(result.gstAmount);
    setTdsAmount(result.tdsAmount);
    setNetPayable(result.netPayable);
  }, [baseFee, gstPercentage, includeGst, tdsPercentage, includeTds]);

  // Fetch campaign-influencer relationships for selected influencer
  useEffect(() => {
    const fetchCampaignInfluencersForInfluencer = async () => {
      if (!influencerId) {
        setFilteredCampaignInfluencers([]);
        return;
      }

      setLoadingCampaigns(true);
      try {
        const { data, error } = await supabase
          .from("campaign_influencers")
          .select(
            `
            id,
            campaign_id,
            campaigns!inner (
              id,
              name
            )
          `
          )
          .eq("influencer_id", influencerId);

        if (error) {
          console.error(
            "Error fetching campaign-influencers for influencer:",
            error
          );
          toast({
            title: "Error loading campaigns",
            description:
              "Failed to load campaigns for the selected influencer.",
            variant: "destructive",
          });
          setFilteredCampaignInfluencers([]);
          return;
        }

        const formattedData: CampaignInfluencerFromDB[] = (data || []).map(
          (item: any) => ({
            id: item.id,
            campaign_id: item.campaign_id,
            campaigns: item.campaigns,
          })
        );

        setFilteredCampaignInfluencers(formattedData);

        if (formattedData.length === 0) {
          toast({
            title: "No campaigns found",
            description:
              "This influencer is not associated with any campaigns yet.",
            variant: "default",
          });
        }
      } catch (error) {
        console.error("Error fetching campaign-influencers:", error);
        toast({
          title: "Error loading campaigns",
          description: "An unexpected error occurred while loading campaigns.",
          variant: "destructive",
        });
        setFilteredCampaignInfluencers([]);
      } finally {
        setLoadingCampaigns(false);
      }
    };

    fetchCampaignInfluencersForInfluencer();
  }, [influencerId]);

  // Reset campaign selection when influencer changes
  useEffect(() => {
    setCampaignInfluencerId("");
  }, [influencerId]);

  function validate(): PaymentValidationErrors {
    const newErrors: PaymentValidationErrors = {};
    if (!influencerId) newErrors.influencerId = "Influencer is required";
    if (!campaignInfluencerId)
      newErrors.campaignInfluencerId = "Campaign is required";
    if (!baseFee || isNaN(Number(baseFee)) || Number(baseFee) <= 0)
      newErrors.baseFee = "Base fee must be greater than 0";
    if (
      includeGst &&
      (!gstPercentage ||
        isNaN(Number(gstPercentage)) ||
        Number(gstPercentage) < 0)
    ) {
      newErrors.gstPercentage = "GST percentage must be a positive number";
    }
    if (
      includeTds &&
      (!tdsPercentage ||
        isNaN(Number(tdsPercentage)) ||
        Number(tdsPercentage) < 0)
    ) {
      newErrors.tdsPercentage = "TDS percentage must be a positive number";
    }
    if (!status) newErrors.status = "Status is required";
    if (!dueDate && status === "pending")
      newErrors.dueDate = "Due date is required for pending payments";
    if (paymentDate && paymentDate > new Date())
      newErrors.paymentDate = "Payment date cannot be in the future";
    if (dueDate && dueDate < new Date() && status === "pending") {
      setStatus("overdue");
    }
    return newErrors;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const baseFeeNum = parseFloat(baseFee);
      const gstPercentNum = parseFloat(gstPercentage) || 0;
      const tdsPercentNum = parseFloat(tdsPercentage) || 0;

      const finalGstAmount = includeGst
        ? (baseFeeNum * gstPercentNum) / 100
        : 0;
      const finalTdsAmount = includeTds
        ? (baseFeeNum * tdsPercentNum) / 100
        : 0;

      const baseDbAmount = convertDisplayToDb(baseFeeNum, userCountry);
      const dbGstAmount = includeGst
        ? convertDisplayToDb(finalGstAmount, userCountry)
        : undefined;
      const dbTdsAmount = includeTds
        ? convertDisplayToDb(finalTdsAmount, userCountry)
        : undefined;

      const paymentData = {
        campaign_influencer_id: campaignInfluencerId,
        amount: baseDbAmount,
        status: status as PaymentStatus,
        payment_mode: mode as PaymentMode,
        due_date: dueDate ? format(dueDate, "yyyy-MM-dd") : undefined,
        payment_date: paymentDate
          ? format(paymentDate, "yyyy-MM-dd")
          : undefined,
        transaction_id: transactionId || undefined,
        notes: notes || undefined,
        gst_applied: includeGst,
        gst_amount: dbGstAmount,
        tds_deducted: includeTds,
        tds_amount: dbTdsAmount,
      };

      await addPaymentMutation.mutateAsync(paymentData);

      resetForm();
      onClose();
      onSuccess?.();
    } catch (error) {
      console.error("Error adding payment:", error);
    }
  }

  function resetForm() {
    setInfluencerId("");
    setCampaignInfluencerId("");
    setBaseFee("");
    setStatus("pending");
    setPaymentDate(undefined);
    setDueDate(undefined);
    setMode("upi");
    setTransactionId("");
    setNotes("");
    setIncludeGst(false);
    setGstPercentage("18.0");
    setGstAmount(0);
    setIncludeTds(false);
    setTdsPercentage("10.0");
    setTdsAmount(0);
    setNetPayable(0);
    setErrors({});
  }

  function closeAndReset() {
    resetForm();
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={closeAndReset}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Payment</DialogTitle>
          <DialogDescription>
            Create a new payment record for an influencer-campaign relationship.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <InfluencerCampaignSelector
            influencers={influencers}
            loadingInfluencers={loadingInfluencers}
            influencerId={influencerId}
            onInfluencerChange={setInfluencerId}
            campaignInfluencerId={campaignInfluencerId}
            onCampaignInfluencerChange={setCampaignInfluencerId}
            filteredCampaignInfluencers={filteredCampaignInfluencers}
            loadingCampaigns={loadingCampaigns}
            baseFee={baseFee}
            onBaseFeeChange={setBaseFee}
            userCountry={userCountry}
            errors={errors}
          />

          <TaxBreakdownCard
            baseFee={baseFee}
            includeGst={includeGst}
            onIncludeGstChange={setIncludeGst}
            gstPercentage={gstPercentage}
            onGstPercentageChange={setGstPercentage}
            gstAmount={gstAmount}
            includeTds={includeTds}
            onIncludeTdsChange={setIncludeTds}
            tdsPercentage={tdsPercentage}
            onTdsPercentageChange={setTdsPercentage}
            tdsAmount={tdsAmount}
            netPayable={netPayable}
            userCountry={userCountry}
            errors={errors}
          />

          <PaymentScheduleFields
            status={status}
            onStatusChange={setStatus}
            dueDate={dueDate}
            onDueDateChange={setDueDate}
            paymentDate={paymentDate}
            onPaymentDateChange={setPaymentDate}
            mode={mode}
            onModeChange={setMode}
            transactionId={transactionId}
            onTransactionIdChange={setTransactionId}
            notes={notes}
            onNotesChange={setNotes}
            errors={errors}
          />

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary" onClick={closeAndReset}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              className="bg-[#A655F8] hover:bg-[#A655F8] text-white font-medium px-4 py-2 rounded-md"
              disabled={addPaymentMutation.isPending}
            >
              {addPaymentMutation.isPending ? "Adding..." : "Add Payment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddPaymentModal;
